'use client';

import { c as _c } from "react/compiler-runtime";
import { ApolloLink, CombinedGraphQLErrors, ServerError, ServerParseError } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { RetryLink } from '@apollo/client/link/retry';
import { ApolloClient, ApolloNextAppProvider, InMemoryCache } from '@apollo/client-integration-nextjs';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLErrorResolver, NotificationsActionCreator, NotificationType } from '@eon.cz/apollo13-frontend-common';
import { Dispatch } from '@reduxjs/toolkit';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { GraphQLError, GraphQLFormattedError } from 'graphql';
import { FormattedMessage } from 'react-intl';
import { AuthActionCreator } from '@components/Auth/action/AuthAction';
import store from '@lib/createStore';
import fragment from '../../node_modules/@eon.cz/apollo13-graphql-certifikovani-partneri/lib/fragmentTypesV3.json';
const serverStatusCode = [500, 503];
const clientStatusCode = [401, 403];
export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';
const removeTypenameLink = new RemoveTypenameFromVariablesLink();

// Název operace v hlavičce, aby šlo blokované/pomalé volání dohledat v logu proxy, nginx a WAF (tělo požadavku tam nevidíme)
const operationNameLink = new ApolloLink((operation, forward) => {
  operation.setContext(({
    headers
  }: {
    headers?: Record<string, string>;
  }) => ({
    headers: {
      ...headers,
      'X-Apollo-Operation-Name': operation.operationName || 'graphql'
    }
  }));
  return forward(operation);
});
const retryLink = new RetryLink({
  delay: {
    initial: 500,
    jitter: false
  },
  attempts: {
    max: 2,
    retryIf: (error, operation) => operation.getContext().retryOnce === true && (!ServerError.is(error) || error.statusCode >= 500)
  }
});
const errorLink = (dispatch: Dispatch) => new ErrorLink(({
  error,
  operation
}) => {
  const context = operation.getContext();
  const httpError = ServerError.is(error) || ServerParseError.is(error) ? error : undefined;
  const getLogHeaders = (headers?: HeadersInit) => headers ? Object.fromEntries(Array.from(new Headers(headers), ([name, value]) => [name, /authorization|cookie|token|api[-_]?key|secret/i.test(name) ? '[REDACTED]' : value])) : undefined;
  console.error('[Apollo Client]', {
    operationName: operation.operationName,
    error,
    graphQLErrors: CombinedGraphQLErrors.is(error) ? error.errors : undefined,
    statusCode: httpError?.statusCode,
    bodyText: httpError?.bodyText,
    requestHeaders: getLogHeaders(context.headers),
    responseHeaders: getLogHeaders((httpError?.response ?? context.response)?.headers)
  });
  if (CombinedGraphQLErrors.is(error)) {
    const invalidLogin = (error.errors as ReadonlyArray<GraphQLFormattedError & {
      code?: string;
    }>).reduce((res, error) => error.code === 'INVALID_LOGIN' ? error : res, {} as GraphQLError);
    const isInvalidLogin = invalidLogin?.code === 'INVALID_LOGIN';
    if (isInvalidLogin) {
      AuthActionCreator(dispatch).logout(apolloClient, invalidLogin.code);
      return;
    }
    GraphQLErrorResolver.resolveErrors(dispatch, error.errors);
    // If there are GraphQL errors, ignore network
  } else {
    if (ServerError.is(error)) {
      const code = error.statusCode;
      const addNotification = NotificationsActionCreator(dispatch).addNotification;
      const result = error.bodyText;
      if (clientStatusCode.includes(code)) {
        AuthActionCreator(dispatch).logout(apolloClient, result);
        return;
      }
      if (result === 'INVALID_TOKEN' || result === 'TOKEN_INACTIVITY_EXCEEDED' || result === 'REVOKED_TOKEN' || result === 'INVALID_LOGIN') {
        AuthActionCreator(dispatch).logout(apolloClient, result);
        return;
      }
      if ('statusCode' in error && serverStatusCode.includes(error.statusCode)) {
        const error = code === 503 ? 'loginDisabled' : 'SERVER_ERROR';
        AuthActionCreator(dispatch).logout(apolloClient, error);
        return;
      }
      // Other error
      addNotification({
        type: NotificationType.ERROR,
        text: <FormattedMessage id="chybaSite" />
      });
    }
  }
});
export let apolloClient: ApolloClient;
export const createClient = () => {
  /**
   * Middleware that adds info about GQL call to Sentry breadcrumbs
   */

  const dispatch = store.dispatch;
  const httpLink = new UploadHttpLink({
    uri: '/api/graphql',
    fetchOptions: {
      credentials: 'same-origin'
    }
  }) as unknown as ApolloLink;
  const link = typeof window !== 'undefined' ? ApolloLink.split(({
    query
  }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && (definition.operation === 'query' || definition.operation === 'mutation');
  }, httpLink) : httpLink;
  apolloClient = new ApolloClient({
    devtools: {
      enabled: typeof window !== 'undefined'
    },
    assumeImmutableResults: false,
    link: ApolloLink.from([errorLink(dispatch), retryLink, removeTypenameLink, operationNameLink, link]),
    cache: new InMemoryCache({
      possibleTypes: fragment.possibleTypes,
      typePolicies: {
        Query: {
          fields: {
            zadosti: {
              merge: true
            }
          }
        },
        SmlouvaOdbernehoMista: {
          fields: {
            elektrina: {
              merge: true
            }
          }
        }
      }
    }),
    defaultOptions: {
      mutate: {
        errorPolicy: 'all'
      },
      query: {
        fetchPolicy: 'network-only'
      },
      watchQuery: {
        nextFetchPolicy(lastFetchPolicy) {
          if (lastFetchPolicy === 'cache-and-network' || lastFetchPolicy === 'network-only') {
            return 'cache-first';
          }
          return lastFetchPolicy;
        }
      }
    }
  });
  return apolloClient;
};

// you need to create a component to wrap your app in
export function ApolloWrapper(t0) {
  const $ = _c(5);
  const {
    children,
    nonce
  } = t0;
  let t1;
  if ($[0] !== nonce) {
    t1 = {
      nonce
    };
    $[0] = nonce;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <ApolloNextAppProvider extraScriptProps={t1} makeClient={createClient}>{children}</ApolloNextAppProvider>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}